import React, { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  isSiteFotoowlPattern,
  getDomainPattern,
  getUserIdentifier,
} from "@/routes/routingUtils";

interface TenantContextValue {
  tenant: string | null;
  isPathBased: boolean;
  userIdentifier: string;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within TenantProvider");
  }
  return context;
};

interface TenantProviderProps {
  children: React.ReactNode;
  /**
   * Optional tenant from path (used when isPathBased is true)
   * If provided, this takes precedence over path extraction
   */
  initialTenantFromPath?: string;
  /**
   * If true, forces path-based tenant detection
   * If false or undefined, auto-detects based on hostname
   */
  isPathBased?: boolean;
}

/**
 * TenantProvider component that determines tenant context based on routing pattern:
 * - Path-based: site.fotoowl.ai/:tenant/... (tenant in URL path)
 * - Subdomain-based: <tenant>.fotoowl.ai/... (tenant in hostname)
 *
 * Provides tenant context to child components via useTenant hook.
 */
export const TenantProvider: React.FC<TenantProviderProps> = ({
  children,
  initialTenantFromPath,
  isPathBased: forcePathBased,
}) => {
  const location = useLocation();

  const tenantContext = useMemo(() => {
    // Determine if we're using path-based routing
    const isPathBasedTenant =
      forcePathBased !== undefined ? forcePathBased : isSiteFotoowlPattern();

    let tenant: string | null = null;
    let userIdentifier: string = "";

    if (isPathBasedTenant) {
      // Path-based: extract tenant from path or use provided initialTenantFromPath
      tenant = initialTenantFromPath || getDomainPattern() || null;
      userIdentifier = getUserIdentifier();
    } else {
      // Subdomain-based: extract tenant from hostname
      userIdentifier = getUserIdentifier();
      // Extract tenant from subdomain (e.g., "tenantname" from "tenantname.fotoowl.ai")
      const hostname =
        typeof window !== "undefined" ? window.location.hostname : "";
      if (hostname && !hostname.includes("site.fotoowl.ai")) {
        // For subdomain or custom domain, tenant is the hostname
        tenant = hostname;
      }
    }

    return {
      tenant,
      isPathBased: isPathBasedTenant,
      userIdentifier,
    };
  }, [initialTenantFromPath, forcePathBased, location.pathname]);

  return (
    <TenantContext.Provider value={tenantContext}>
      {children}
    </TenantContext.Provider>
  );
};
